import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { LocalizationProvider, PickersDayProps } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  Stack,
  Typography,
} from '@mui/material';
import { Box as NBox } from 'new/elements/Box';
import { DayPicker } from '@mui/x-date-pickers/internals';
import type { SlideDirection } from '@mui/x-date-pickers/CalendarPicker/PickersSlideTransition';
import { KeyboardArrowLeftRounded, KeyboardArrowRightRounded } from '@mui/icons-material';

import { Day } from './Day';
import styles from './DateRangePicker.module.scss';

const defaultIntervals = [
  {
    title: 'Today',
    key: 'today',
    startDate: new Date(),
    endDate: new Date(),
  },
  {
    title: 'Yesterday',
    key: 'yesterday',
    startDate: moment().subtract('1', 'day').toDate(),
    endDate: moment().subtract('1', 'day').toDate(),
  },
  {
    title: 'Last 7 days',
    key: 'last7',
    startDate: moment().subtract('7', 'days').toDate(),
    endDate: new Date(),
  },
  {
    title: 'Last 30 days',
    key: 'last30',
    startDate: moment().subtract('30', 'days').toDate(),
    endDate: new Date(),
  },
  {
    title: 'This month',
    key: 'thisMonth',
    startDate: moment().startOf('month').toDate(),
    endDate: moment().endOf('month').toDate(),
  },
  {
    title: 'Previous month',
    key: 'previousMonth',
    startDate: moment().subtract(1, 'month').startOf('month').toDate(),
    endDate: moment().subtract(1, 'month').endOf('month').toDate(),
  },
] as DateRangeInterval[];

export type DateRangeInterval = {
  title: string;
  key: string;
  startDate: Date;
  endDate: Date;
};

interface IRangePicker {
  startDate?: Date | null;
  endDate?: Date | null;
  onChange?: (sDate: Date | null, eDate: Date | null) => void;
  intervals?: DateRangeInterval[];
  defaultInterval?: string;
  minDate?: Date;
  maxDate?: Date;
  // When true, the picker speaks UTC days: callers pass/receive UTC-midnight
  // dates, but the calendar renders/compares in local time so the highlighted
  // cells line up with the labels for users in any time zone. See toCal/fromCal.
  utc?: boolean;
}

// The MUI calendar renders and compares day cells in local time. When `utc` is
// on, our callers deal in UTC-day instants (00:00Z), so we bridge at the edges:
// map a UTC day to a local-midnight date with the SAME Y/M/D for the calendar,
// and map a clicked local date back to a UTC-midnight day on the way out.
export const utcToLocalNaive = (d?: Date | null): Date | null =>
  d ? new Date(moment.utc(d).year(), moment.utc(d).month(), moment.utc(d).date()) : null;
export const localNaiveToUtc = (d?: Date | null): Date | null =>
  d ? new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())) : null;

export const DateRangePicker = ({
  minDate = moment('1 January 1900').toDate(),
  maxDate = moment('31 December 3050').toDate(),
  startDate: sDateProp = null,
  endDate: eDateProp = null,
  onChange,
  intervals = defaultIntervals,
  utc = false,
}: IRangePicker) => {
  // Everything inside the component works in "calendar frame" (local). In utc
  // mode we convert inbound dates to that frame and convert selections back out.
  const toCal = (d?: Date | null): Date | null => (utc ? utcToLocalNaive(d) : (d ?? null));
  const fromCal = (d?: Date | null): Date | null => (utc ? localNaiveToUtc(d) : (d ?? null));

  const sDate = toCal(sDateProp);
  const eDate = toCal(eDateProp);
  const calMinDate = toCal(minDate);
  const calMaxDate = toCal(maxDate);
  const [selectedStartDate, setSelectedStartDate] = React.useState<Date | null>(sDate);
  const [selectedEndDate, setSelectedEndDate] = React.useState<Date | null>(eDate);
  const [startDate, setStartDate] = React.useState<Date | null>(sDate || null);
  const [endDate, setEndDate] = React.useState<Date | null>(eDate || null);
  const [selectedDays, setSelectedDays] = React.useState<Date[]>([]);
  const [month, setMonth] = React.useState<Date>(new Date());
  const [direction, setDirection] = React.useState<SlideDirection>('left');
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = React.useState(false);

  const startTimestamp = sDate ? sDate.getTime() : null;
  const endTimestamp = eDate ? eDate.getTime() : null;

  useEffect(() => {
    const nextStart = startTimestamp != null ? new Date(startTimestamp) : null;
    const nextEnd = endTimestamp != null ? new Date(endTimestamp) : null;
    setSelectedStartDate(nextStart);
    setSelectedEndDate(nextEnd);
    setStartDate(nextStart);
    setEndDate(nextEnd);
  }, [startTimestamp, endTimestamp]);

  const selectDate = (d) => {
    if (!startDate && !endDate) {
      setStartDate(d);
      return;
    }

    if (!endDate) {
      if (moment(d).isBefore(startDate)) {
        setStartDate(d);
        setEndDate(startDate);
      } else {
        setEndDate(d);
      }
      return;
    }

    setStartDate(d);
    setEndDate(null);
  };

  const handleClick = (event) => {
    setOpen(true);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(false);
    setStartDate(selectedStartDate);
    setEndDate(selectedEndDate);
  };

  useEffect(() => {
    if (!startDate && !endDate) {
      setSelectedDays([null, null]);
    }
    if (startDate && !endDate) {
      setSelectedDays([startDate]);
    }
    if (startDate && endDate) {
      const sDays: Array<Date> = [];
      const diff = moment(endDate).diff(startDate, 'days');
      for (let i = 0; i <= diff; i++) {
        sDays.push(moment(startDate).add(i, 'days').toDate());
      }

      setSelectedDays(sDays);
    }
  }, [startDate, endDate]);

  const selectInterval = (interval) => () => {
    const start = toCal(interval.startDate);
    const end = toCal(interval.endDate);
    setStartDate(start);
    setEndDate(end);
    onApply(start, end);
  };

  const onClickPrevMonth = () => {
    setDirection('right');
    setMonth(moment(month).subtract(1, 'month').toDate());
  };

  const onClickNextMonth = () => {
    setDirection('left');
    setMonth(moment(month).add(1, 'month').toDate());
  };

  const onClickApply = (_) => {
    onApply();
  };

  const onApply = (sd?, ed?) => {
    setOpen(false);
    const start = sd || startDate;
    const end = ed || endDate;
    setSelectedStartDate(start);
    setSelectedEndDate(end);
    // Internal state is in calendar (local) frame; convert back out to UTC days.
    onChange?.(fromCal(start), fromCal(end));
  };

  const renderWeekPickerDay = (
    dateJs: moment.Moment,
    selectedDates: Array<any | null>,
    pickersDayProps: PickersDayProps<any>,
  ) => <Day dateJs={dateJs} selectedDates={selectedDates} pickersDayProps={pickersDayProps} />;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <NBox className={styles.boxContainer} onClick={handleClick}>
        <Stack
          direction="row"
          alignItems="center"
          padding="4px"
          paddingRight="12px"
          paddingLeft="12px"
          gap="16px"
        >
          <Typography variant={'body1'}>
            {startDate ? moment(startDate).format('DD MMM YYYY') : 'Select date'}
          </Typography>{' '}
          -
          <Typography variant={'body1'}>
            {endDate ? moment(endDate).format('DD MMM YYYY') : 'Select date'} (UTC)
          </Typography>
        </Stack>
      </NBox>

      <Menu open={open} onClose={handleClose} anchorEl={anchorEl}>
        <Stack>
          <Box sx={{ display: 'flex', width: 'auto' }}>
            <Box sx={{ display: 'flex', pt: 2, pl: 2 }}>
              <Box className={[styles.pickerContainer, styles.pickerContainerLeft].join(' ')}>
                <Grid container justifyContent="space-between" className={styles.monthContainer}>
                  <Grid item sx={{ width: '40px' }}>
                    <IconButton onClick={onClickPrevMonth}>
                      <KeyboardArrowLeftRounded />
                    </IconButton>
                  </Grid>
                  <Grid item sx={{ textAlign: 'center' }}>
                    <Typography variant="body2">{moment(month).format('MMMM')}</Typography>
                  </Grid>
                  <Grid item sx={{ width: '40px' }} />
                </Grid>
                <DayPicker
                  disablePast={false}
                  disableFuture={false}
                  currentMonth={month}
                  selectedDays={selectedDays}
                  renderDay={renderWeekPickerDay}
                  onSelectedDaysChange={selectDate}
                  focusedDay={new Date()}
                  isMonthSwitchingAnimating
                  onFocusedDayChange={() => null}
                  onMonthSwitchingAnimationEnd={() => null}
                  reduceAnimations={false}
                  slideDirection={direction}
                  loading={false}
                  minDate={calMinDate}
                  maxDate={calMaxDate}
                />
              </Box>
              <Box className={styles.pickerContainer}>
                <Grid className={styles.monthContainer} container justifyContent="space-between">
                  <Grid item sx={{ width: '40px' }} />
                  <Grid item sx={{ textAlign: 'center' }}>
                    <Typography variant="body2">
                      {moment(month).add(1, 'month').format('MMMM')}
                    </Typography>
                  </Grid>
                  <Grid item sx={{ width: '40px' }}>
                    <IconButton onClick={onClickNextMonth}>
                      <KeyboardArrowRightRounded />
                    </IconButton>
                  </Grid>
                </Grid>
                <DayPicker
                  disablePast={false}
                  disableFuture={false}
                  renderDay={renderWeekPickerDay}
                  currentMonth={moment(month).add(1, 'month').toDate()}
                  selectedDays={selectedDays}
                  onSelectedDaysChange={selectDate}
                  focusedDay={new Date()}
                  isMonthSwitchingAnimating
                  onFocusedDayChange={() => null}
                  onMonthSwitchingAnimationEnd={() => null}
                  reduceAnimations={false}
                  slideDirection={direction}
                  loading={false}
                  minDate={calMinDate}
                  maxDate={calMaxDate}
                />
              </Box>
            </Box>
            <Box
              sx={{
                minWidth: '200px',
                pt: 2,
                borderLeft: '1px solid rgba(0,0,0,0.1)',
              }}
            >
              <List sx={{ p: 0 }}>
                {intervals.map((interval) => (
                  <ListItem key={interval.key} disablePadding onClick={selectInterval(interval)}>
                    <ListItemButton>
                      <ListItemText primary={interval.title} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
          <Box
            sx={{
              borderTop: '1px solid rgba(0,0,0,0.1)',
              textAlign: 'right',
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <div
              className="timezone-info"
              style={{
                padding: 'var(--spacing-md)',
                color: 'rgba(0, 0, 0, 0.6)',
                fontSize: '0.875rem',
              }}
            >
              {' '}
              All times are shown in UTC
            </div>
            <Button color="primary" variant="outlined" onClick={onClickApply}>
              Apply Date Range
            </Button>
          </Box>
        </Stack>
      </Menu>
    </LocalizationProvider>
  );
};
